import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { SideBarProfileField } from "pages/Profile";
import { assignKeysToItems, SideBarItemList } from "common/components";
import { ReactComponent as MenuIcon } from "assets/icons/icon-menu-24.svg";
import { ReactComponent as LogOutIcon } from "assets/icons/icon-logout-24.svg";

import {
  sideBarAnimationDuration,
  HEADER_HEIGHT,
  sideBarIconSize,
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_EXPANDED,
} from "common/const/const";
import { DataLoadingLayout } from "common/utils/DataLoadingLayout";
import { apiRequest } from "common/utils/api";
import { REQUEST_METHOD } from "common/enum/RequestMethod";
import { SideBarItem, SideBarItemWrapper } from "./SideBarItem";

const SideBar = ({
  access,
  clubImageRef,
  roleName,
  clubName,
  clubId,
  isProfile = true,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const { selectedKey: initialKey, sideBarMenuList } = useMemo(() => {
    const filtered = SideBarItemList.filter((_, index) => access[index]);
    const list = assignKeysToItems(filtered);

    const findKey = (items, pathname) => {
      for (const item of items) {
        if (item.link === pathname) return item.key;
        if (item.children) {
          const found = findKey(item.children, pathname);
          if (found) return found;
        }
      }
      return null;
    };

    return {
      selectedKey: findKey(list, location.pathname)?.substring(0, 1),
      sideBarMenuList: list,
    };
  }, [access, location.pathname]);

  const [selectedKey, setSelectedKey] = useState(initialKey);
  const handleItemClick = (key, expandable) => {
    setSelectedKey(key);
    if (!isOpen && expandable) setIsOpen(true);
  };

  useEffect(() => {
    if (initialKey) {
      setSelectedKey(initialKey);
    }
  }, [initialKey]);

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, [location.pathname]);

  return (
    <>
      <SideBarMenu $isOpen={isOpen}>
        <SideBarHeader>
          <SideBarButton
            onClick={() => {
              setIsOpen(!isOpen);
            }}>
            <MenuIcon width={sideBarIconSize} />
          </SideBarButton>
        </SideBarHeader>

        {loading ? (
          <SideBarItemWrapper
            style={{ justifyContent: "center", height: "8rem" }}>
            <DataLoadingLayout loading={true} />
          </SideBarItemWrapper>
        ) : (
          <>
            {isProfile && (
              <SideBarProfileField
                clubName={clubName}
                isOpen={isOpen}
                clubImageRef={clubImageRef}
                roleName={roleName}
              />
            )}
            <SideBarContent>
              {sideBarMenuList.map((item) => {
                return (
                  <SideBarItem
                    key={item.key}
                    item={item}
                    onClick={handleItemClick}
                    selectedKey={selectedKey}
                    isOpen={isOpen}
                  />
                );
              })}
            </SideBarContent>
          </>
        )}

        <SideBarLogOut>
          <SideBarItem
            key={null}
            item={{ label: "Log out", icon: LogOutIcon }}
            selectedKey={null}
            onClick={() => {
              sessionStorage.clear();
              apiRequest({
                method: REQUEST_METHOD.DELETE,
                url: "/api/v1.0/sign-out",
              });
              window.location.href = "/";
            }}
          />
        </SideBarLogOut>
      </SideBarMenu>
    </>
  );
};

export const SideBarBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: var(--LightGray);
  z-index: -1;
`;

const SideBarMenu = styled.ul`
  display: flex;
  flex-direction: column;
  height: 100vh;

  list-style: none;
  padding: 0;
  margin: 0;
  flex-shrink: 0;
  align-items: flex-start;
  position: relative;

  width: ${({ $isOpen }) => ($isOpen ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH)};
  border-right: 0.5px solid var(--Gray);
  background-color: var(--White);

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  transition: ${sideBarAnimationDuration} ease-in-out;
  z-index: 100;
`;

const SideBarHeader = styled.div`
  display: flex;
  box-sizing: border-box;
  flex-direction: row;
  width: 100%;
  height: ${HEADER_HEIGHT};
  align-items: center;
  justify-content: space-between;
  padding: ${sideBarIconSize};
  border-bottom: 0.5px solid var(--Gray);
`;

const SideBarButton = styled.button`
  background-color: var(--White);
  border: none;
  cursor: pointer;
  padding: 0;

  ${({ $animated, $isOpen }) =>
    $animated &&
    css`
      opacity: ${$isOpen ? 1 : 0};
      pointer-events: ${$isOpen ? "auto" : "none"};
      transform: ${$isOpen
        ? "translateX(0)"
        : `translateX(-${sideBarIconSize})`};
      transition:
        opacity 0.1s ease,
        transform ${sideBarAnimationDuration} ease;
    `}
`;

const SideBarContent = styled.div`
  flex: 1;
  width: 100%;
  overflow-y: auto;
`;

const SideBarLogOut = styled.li`
  width: 100%;
  height: 4rem;
  display: flex;
  align-items: center;
  background-color: white;
  border-top: 0.5px solid var(--Gray);
  flex-shrink: 0;
  box-shadow: 0 -1px 3px rgba(0, 0, 0, 0.03);
`;

export { SideBar };
